import {Observable, of} from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {catchError, map, mergeMap, switchMap} from "rxjs/operators";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import {ajax} from "rxjs/ajax";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError} from "../common/EventNotificationActions";
import {Nullable} from "../../models/common/Nullable";
import {getInitialViewSettings, UserViewSettings, UserViewSettingsProp} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {IncomingInvoiceModule} from "../../models/accounting/IncomingInvoice";
import {IncomingInvoice, IncomingInvoiceId, IncomingInvoiceProp} from "../../models/accounting/IncomingInvoice";
import {
    getUrlParamsFromViewSettings,
    multipartHeaders,
    RouteConfig,
    RouteMethods,
    toFormDataEncode
} from "../../config/RouteConfig";
import {Identifier} from "../../models/common/Identifier";
import {Sensitive} from "../../models/common/Sensitive";
import {Money} from "../../models/accounting/Finance";
import {ProjectId} from "../../models/projects/Project";
import {InvoiceType} from "../../models/accounting/InvoiceType";
import {sendUserToPage} from "../common/UserViewSettingsActions";
import {FileData} from "../../models/common/FileData";
import {
    DeleteIncomingInvoicePaymentActionTypes,
    InsertIncomingInvoicePaymentActionTypes
} from "./IncomingInvoicePaymentActions";
import {dateAsTimestamp} from "../../helpers/DateTimeHelpers";



/** backend api path */
function apiPath( id: Nullable< number > = null ): string {
    const uri = 'invoices/incoming/'
    if( id === null ) {
        return APP_CONFIG.baseWeb + uri;
    } else {
        return APP_CONFIG.baseWeb + uri  + id;
    }
}


/** union of all Bill related action types */
export type IncomingInvoiceViewActionTypes = FetchIncomingInvoicesActionTypes
    | FetchIncomingInvoicesPrePaymentByProjectActionTypes
    | CreateIncomingInvoiceActionTypes
    | UpdateIncomingInvoiceActionTypes
    | FetchIncomingInvoicesCheckFinalActionTypes
    | FetchIncomingInvoiceByIdActionTypes
    | MarkCanceledIncomingInvoiceActionTypes
    | MarkFinalizedIncomingInvoiceActionTypes
    | InsertIncomingInvoicePaymentActionTypes
    | DeleteIncomingInvoicePaymentActionTypes
    | FetchInInvoiceFilesByIdActionTypes
    | DeleteIncomingInvoiceByIdActionTypes
    


/**
 * fetch list of bills action types
 */
export interface FetchIncomingInvoicesAction extends UserViewSettingsProp, ReduxAction< typeof FETCH_INCOMING_INVOICES > {}
export interface FetchIncomingInvoicesSuccessAction extends ReduxAction<typeof FETCH_INCOMING_INVOICES_SUCCESS > {
    payload: ApiReturnType< IncomingInvoice[] >
    
}
export interface FetchIncomingInvoicesErrorAction extends  ErrorAction< typeof FETCH_INCOMING_INVOICES_ERROR >{}

export type FetchIncomingInvoicesActionTypes = FetchIncomingInvoicesAction | FetchIncomingInvoicesErrorAction | FetchIncomingInvoicesSuccessAction

/**
 * redux action constants
 */
export const FETCH_INCOMING_INVOICES = "FETCH_INCOMING_INVOICES";
export const FETCH_INCOMING_INVOICES_SUCCESS = "FETCH_INCOMING_INVOICES_SUCCESS";
export const FETCH_INCOMING_INVOICES_ERROR = "FETCH_INCOMING_INVOICES_ERROR";

/**
 * redux action functions
 */
export function fetchIncomingInvoices( userViewSettings: UserViewSettings ) : FetchIncomingInvoicesAction {
    return { type: FETCH_INCOMING_INVOICES, userViewSettings };
}
export function fetchIncomingInvoicesSuccess( payload: ApiReturnType< IncomingInvoice[] >) : FetchIncomingInvoicesSuccessAction {
    return { type: FETCH_INCOMING_INVOICES_SUCCESS, payload };
}
export function fetchIncomingInvoicesError( error: Error ): FetchIncomingInvoicesErrorAction {
    return { type: FETCH_INCOMING_INVOICES_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchIncomingInvoicesEpic(
    action$: Observable< FetchIncomingInvoicesAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_INCOMING_INVOICES),
        switchMap(action => {
            const path = `${ apiPath() }${  getUrlParamsFromViewSettings( action.userViewSettings ) }`;

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchIncomingInvoicesSuccess(
                    response.response as ApiReturnType< IncomingInvoice[] >
                )),
                catchError(error => of(
                    fetchIncomingInvoicesError( error),
                    eventNotificationError(error, store$, IncomingInvoiceModule.identifier, "fetchIncomingInvoices")
                ))
            );

        })
    );
}




/**
 * fetch pre payments by project
 */
export interface FetchIncomingInvoicesPrePaymentByProjectAction extends  ReduxAction< typeof FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT > {
    projectId: Identifier<number>
}
export interface FetchIncomingInvoicesPrePaymentByProjectSuccessAction extends ReduxAction<typeof FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS > {
    payload: Sensitive<Money>

}
export interface FetchIncomingInvoicesPrePaymentByProjectErrorAction extends  ErrorAction< typeof FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR >{}

export type FetchIncomingInvoicesPrePaymentByProjectActionTypes = FetchIncomingInvoicesPrePaymentByProjectAction | FetchIncomingInvoicesPrePaymentByProjectErrorAction | FetchIncomingInvoicesPrePaymentByProjectSuccessAction

/**
 * redux action constants
 */
export const FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT = "FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT";
export const FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS = "FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS";
export const FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR = "FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR";

/**
 * redux action functions
 */
export function fetchIncomingInvoicesPrePaymentByProject( projectId: Identifier<number> ) : FetchIncomingInvoicesPrePaymentByProjectAction {
    return { type: FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT, projectId };
}
export function fetchIncomingInvoicesPrePaymentByProjectSuccess( payload: Sensitive< Money > ) : FetchIncomingInvoicesPrePaymentByProjectSuccessAction {
    return { type: FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS, payload };
}
export function fetchIncomingInvoicesPrePaymentByProjectError( error: Error ): FetchIncomingInvoicesPrePaymentByProjectErrorAction {
    return { type: FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchIncomingInvoicesPrePaymentByProjectEpic(
    action$: Observable< FetchIncomingInvoicesPrePaymentByProjectAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT),
        switchMap(action => {
            const path = `${ apiPath() }projects/${ action.projectId.rawValue }/payments`;

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchIncomingInvoicesPrePaymentByProjectSuccess(
                    response.response as Sensitive< Money >
                )),
                catchError(error => of(
                    fetchIncomingInvoicesPrePaymentByProjectError( error),
                    eventNotificationError(error, store$, IncomingInvoiceModule.identifier, "fetchIncomingInvoicesPrePaymentByProject")
                ))
            );

        })
    );
}



/**
 * check if the project already has a final invoice 
 */
export interface FetchIncomingInvoicesCheckFinalAction extends ProjectId, ReduxAction< typeof FETCH_INCOMING_INVOICES_CHECK_FINAL > {
}
export interface FetchIncomingInvoicesCheckFinalSuccessAction extends ReduxAction<typeof FETCH_INCOMING_INVOICES_CHECK_FINAL_SUCCESS > {
    payload: number
}
export interface FetchIncomingInvoicesCheckFinalErrorAction extends  ErrorAction< typeof FETCH_INCOMING_INVOICES_CHECK_FINAL_ERROR >{}

export type FetchIncomingInvoicesCheckFinalActionTypes = FetchIncomingInvoicesCheckFinalAction | FetchIncomingInvoicesCheckFinalErrorAction | FetchIncomingInvoicesCheckFinalSuccessAction

/**
 * redux action constants
 */
export const FETCH_INCOMING_INVOICES_CHECK_FINAL = "FETCH_INCOMING_INVOICES_CHECK_FINAL";
export const FETCH_INCOMING_INVOICES_CHECK_FINAL_SUCCESS = "FETCH_INCOMING_INVOICES_CHECK_FINAL_SUCCESS";
export const FETCH_INCOMING_INVOICES_CHECK_FINAL_ERROR = "FETCH_INCOMING_INVOICES_CHECK_FINAL_ERROR";

/**
 * redux action functions
 */
export function fetchIncomingInvoicesCheckFinal( projectId: Identifier< number > ) : FetchIncomingInvoicesCheckFinalAction {
    return { type: FETCH_INCOMING_INVOICES_CHECK_FINAL, projectId };
}
export function fetchIncomingInvoicesCheckFinalSuccess( payload: number ) : FetchIncomingInvoicesCheckFinalSuccessAction {
    return { type: FETCH_INCOMING_INVOICES_CHECK_FINAL_SUCCESS, payload };
}
export function fetchIncomingInvoicesCheckFinalError( error: Error ): FetchIncomingInvoicesCheckFinalErrorAction {
    return { type: FETCH_INCOMING_INVOICES_CHECK_FINAL_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchIncomingInvoicesCheckFinalEpic(
    action$: Observable< FetchIncomingInvoicesCheckFinalAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_INCOMING_INVOICES_CHECK_FINAL),
        switchMap(action => {
            const path = `${ apiPath() }check-final/${ action.projectId.rawValue }`;

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchIncomingInvoicesCheckFinalSuccess(
                    response.response as number
                )),
                catchError(error => of(
                    fetchIncomingInvoicesCheckFinalError( error),
                    eventNotificationError(error, store$, IncomingInvoiceModule.identifier, "fetchIncomingInvoicesCheckFinal")
                ))
            );

        })
    );
}

/**
 * CREATE OUTGOING INVOICE 
 */
export interface CreateIncomingInvoiceAction extends IncomingInvoiceProp, ReduxAction< typeof CREATE_INCOMING_INVOICE > {
    files: File[]
}
export interface CreateIncomingInvoiceSuccessAction extends ReduxAction<typeof CREATE_INCOMING_INVOICE_SUCCESS > {

}
export interface CreateIncomingInvoiceErrorAction extends  ErrorAction< typeof CREATE_INCOMING_INVOICE_ERROR >{}

export type CreateIncomingInvoiceActionTypes = CreateIncomingInvoiceAction | CreateIncomingInvoiceErrorAction | CreateIncomingInvoiceSuccessAction

/**
 * redux action constants
 */
export const CREATE_INCOMING_INVOICE = "CREATE_INCOMING_INVOICE";
export const CREATE_INCOMING_INVOICE_SUCCESS = "CREATE_INCOMING_INVOICE_SUCCESS";
export const CREATE_INCOMING_INVOICE_ERROR = "CREATE_INCOMING_INVOICE_ERROR";

/**
 * redux action functions
 */
export function createIncomingInvoice(
    incomingInvoice: IncomingInvoice,
    files: File[]
) : CreateIncomingInvoiceAction {
    return { type: CREATE_INCOMING_INVOICE, incomingInvoice, files };
}
export function createIncomingInvoiceSuccess() : CreateIncomingInvoiceSuccessAction {
    return { type: CREATE_INCOMING_INVOICE_SUCCESS };
}
export function createIncomingInvoiceError( error: Error ): CreateIncomingInvoiceErrorAction {
    return { type: CREATE_INCOMING_INVOICE_ERROR, error };
}

/**
 * redux observable to create bills from api
 */
export function createIncomingInvoiceEpic(
    action$: Observable< CreateIncomingInvoiceAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(CREATE_INCOMING_INVOICE),
        switchMap(action => {
            const path = apiPath();

            const newInvoice = {
                ...action.incomingInvoice
            };

            //@ts-ignore
            delete newInvoice.totals;


            return ajax(
                APP_CONFIG.getRequestObject(
                    path,
                    RouteMethods.post,
                    toFormDataEncode(
                        'invoice',
                        newInvoice,
                        action.files
                    ),
                    multipartHeaders()
                )
            ).pipe(
                mergeMap( _ => of(
                    createIncomingInvoiceSuccess(),
                    fetchIncomingInvoices( getInitialViewSettings( IncomingInvoiceModule.identifier ) ),
                    sendUserToPage(
                        RouteConfig.incomingInvoice.routes.main.path,
                        IncomingInvoiceModule.identifier
                    )
                )),
                catchError(error => of(
                    createIncomingInvoiceError( error),
                    eventNotificationError(error, store$, IncomingInvoiceModule.identifier, "createIncomingInvoice")
                ))
            );

        })
    );
}

/**
 * UPDATE OUTGOING INVOICE
 */
export interface UpdateIncomingInvoiceAction extends IncomingInvoiceProp, ReduxAction< typeof UPDATE_INCOMING_INVOICE > {
    files: File[]
}
export interface UpdateIncomingInvoiceSuccessAction extends ReduxAction<typeof UPDATE_INCOMING_INVOICE_SUCCESS > {

}
export interface UpdateIncomingInvoiceErrorAction extends  ErrorAction< typeof UPDATE_INCOMING_INVOICE_ERROR >{}

export type UpdateIncomingInvoiceActionTypes = UpdateIncomingInvoiceAction | UpdateIncomingInvoiceErrorAction | UpdateIncomingInvoiceSuccessAction

/**
 * redux action constants
 */
export const UPDATE_INCOMING_INVOICE = "UPDATE_INCOMING_INVOICE";
export const UPDATE_INCOMING_INVOICE_SUCCESS = "UPDATE_INCOMING_INVOICE_SUCCESS";
export const UPDATE_INCOMING_INVOICE_ERROR = "UPDATE_INCOMING_INVOICE_ERROR";

/**
 * redux action functions
 */
export function updateIncomingInvoice(
    incomingInvoice: IncomingInvoice,
    files: File[]
) : UpdateIncomingInvoiceAction {
    return { type: UPDATE_INCOMING_INVOICE,incomingInvoice, files };
}
export function updateIncomingInvoiceSuccess() : UpdateIncomingInvoiceSuccessAction {
    return { type: UPDATE_INCOMING_INVOICE_SUCCESS };
}
export function updateIncomingInvoiceError( error: Error ): UpdateIncomingInvoiceErrorAction {
    return { type: UPDATE_INCOMING_INVOICE_ERROR, error };
}

/**
 * redux observable to update bills from api
 */
export function updateIncomingInvoiceEpic(
    action$: Observable< UpdateIncomingInvoiceAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(UPDATE_INCOMING_INVOICE),
        switchMap(action => {
            const path = apiPath() +"update";

            const newInvoice = {
                ...action.incomingInvoice
            };

            //@ts-ignore
            delete newInvoice.totals;

            return ajax(
                APP_CONFIG.getRequestObject(
                    path,
                    RouteMethods.put,
                    toFormDataEncode( 'invoice', newInvoice, action.files ),
                    multipartHeaders()
                )
            ).pipe(
                mergeMap( _ => of(
                    updateIncomingInvoiceSuccess(),
                    fetchIncomingInvoices( getInitialViewSettings( IncomingInvoiceModule.identifier ) ),
                    sendUserToPage(
                        RouteConfig.incomingInvoice.routes.main.path,
                        IncomingInvoiceModule.identifier
                    )
                )),
                catchError(error => of(
                    updateIncomingInvoiceError( error),
                    eventNotificationError(error, store$, IncomingInvoiceModule.identifier, "updateIncomingInvoice")
                ))
            );

        })
    );
}

/**
 * FETCH DETAIL INVOICE
 */
export interface FetchIncomingInvoiceByIdAction extends IncomingInvoiceId, ReduxAction< typeof FETCH_INCOMING_INVOICE_BY_ID > {

}
export interface FetchIncomingInvoiceByIdSuccessAction extends ReduxAction<typeof FETCH_INCOMING_INVOICE_BY_ID_SUCCESS > {
    payload: IncomingInvoice

}
export interface FetchIncomingInvoiceByIdErrorAction extends IncomingInvoiceId, ErrorAction< typeof FETCH_INCOMING_INVOICE_BY_ID_ERROR >{}

export type FetchIncomingInvoiceByIdActionTypes = FetchIncomingInvoiceByIdAction | FetchIncomingInvoiceByIdErrorAction | FetchIncomingInvoiceByIdSuccessAction

/**
 * redux action constants
 */
export const FETCH_INCOMING_INVOICE_BY_ID = "FETCH_INCOMING_INVOICE_BY_ID";
export const FETCH_INCOMING_INVOICE_BY_ID_SUCCESS = "FETCH_INCOMING_INVOICE_BY_ID_SUCCESS";
export const FETCH_INCOMING_INVOICE_BY_ID_ERROR = "FETCH_INCOMING_INVOICE_BY_ID_ERROR";

/**
 * redux action functions
 */
export function fetchIncomingInvoiceById( incomingInvoiceId: Identifier<number> ) : FetchIncomingInvoiceByIdAction {
    return { type: FETCH_INCOMING_INVOICE_BY_ID, invoiceId: incomingInvoiceId };
}
export function fetchIncomingInvoiceByIdSuccess( payload: IncomingInvoice) : FetchIncomingInvoiceByIdSuccessAction {
    return { type: FETCH_INCOMING_INVOICE_BY_ID_SUCCESS, payload };
}
export function fetchIncomingInvoiceByIdError(
    incomingInvoiceId: Identifier< number >,
    error: Error
): FetchIncomingInvoiceByIdErrorAction {
    return { type: FETCH_INCOMING_INVOICE_BY_ID_ERROR, error, invoiceId: incomingInvoiceId };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchIncomingInvoiceByIdEpic(
    action$: Observable< FetchIncomingInvoiceByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_INCOMING_INVOICE_BY_ID),
        switchMap(action => {
            const path = apiPath( action.invoiceId.rawValue );

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchIncomingInvoiceByIdSuccess( response.response as IncomingInvoice)),
                catchError(error => of(
                    fetchIncomingInvoiceByIdError( action.invoiceId, error),
                    eventNotificationError(error, store$, IncomingInvoiceModule.identifier, "fetchIncomingInvoiceById")
                ))
            );

        })
    );
}

/**
 * change invoice mode to canceled 
 */
export interface MarkCanceledIncomingInvoiceAction extends IncomingInvoiceId, ReduxAction< typeof MARK_CANCELED_INCOMING_INVOICE > {

}
export interface MarkCanceledIncomingInvoiceSuccessAction extends ReduxAction<typeof MARK_CANCELED_INCOMING_INVOICE_SUCCESS > {
}
export interface MarkCanceledIncomingInvoiceErrorAction extends ErrorAction< typeof MARK_CANCELED_INCOMING_INVOICE_ERROR >{}

export type MarkCanceledIncomingInvoiceActionTypes = MarkCanceledIncomingInvoiceAction | MarkCanceledIncomingInvoiceErrorAction | MarkCanceledIncomingInvoiceSuccessAction

/**
 * redux action constants
 */
export const MARK_CANCELED_INCOMING_INVOICE = "MARK_CANCELED_INCOMING_INVOICES";
export const MARK_CANCELED_INCOMING_INVOICE_SUCCESS = "MARK_CANCELED_INCOMING_INVOICES_SUCCESS";
export const MARK_CANCELED_INCOMING_INVOICE_ERROR = "MARK_CANCELED_INCOMING_INVOICES_ERROR";

/**
 * redux action functions
 */
export function markCanceledIncomingInvoice( incomingInvoiceId: Identifier< number > ) : MarkCanceledIncomingInvoiceAction {
    return { type: MARK_CANCELED_INCOMING_INVOICE, invoiceId: incomingInvoiceId };
}
export function markCanceledIncomingInvoiceSuccess() : MarkCanceledIncomingInvoiceSuccessAction {
    return { type: MARK_CANCELED_INCOMING_INVOICE_SUCCESS };
}
export function markCanceledIncomingInvoiceError( error: Error ): MarkCanceledIncomingInvoiceErrorAction {
    return { type: MARK_CANCELED_INCOMING_INVOICE_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function markCanceledIncomingInvoiceEpic(
    action$: Observable< MarkCanceledIncomingInvoiceAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( MARK_CANCELED_INCOMING_INVOICE ),
        switchMap(action => {
            const path = `${ apiPath( action.invoiceId.rawValue ) }/canceled`;

            return ajax(
                APP_CONFIG.getRequestObject( path, RouteMethods.patch )
            ).pipe(
                mergeMap(_ => of(
                    markCanceledIncomingInvoiceSuccess(),
                    fetchIncomingInvoiceById( action.invoiceId ),
                    fetchIncomingInvoices( getInitialViewSettings( IncomingInvoiceModule.identifier ) )
                )),
                catchError(error => of(
                    markCanceledIncomingInvoiceError( error),
                    eventNotificationError(error, store$, IncomingInvoiceModule.identifier, "markCanceledIncomingInvoice")
                ))
            );

        })
    );
}

/**
 * change invoice mode to canceled
 */
export interface MarkFinalizedIncomingInvoiceAction extends IncomingInvoiceId, ReduxAction< typeof MARK_FINALIZED_INCOMING_INVOICE > {

}
export interface MarkFinalizedIncomingInvoiceSuccessAction extends ReduxAction<typeof MARK_FINALIZED_INCOMING_INVOICE_SUCCESS > {
}
export interface MarkFinalizedIncomingInvoiceErrorAction extends ErrorAction< typeof MARK_FINALIZED_INCOMING_INVOICE_ERROR >{}

export type MarkFinalizedIncomingInvoiceActionTypes = MarkFinalizedIncomingInvoiceAction | MarkFinalizedIncomingInvoiceErrorAction | MarkFinalizedIncomingInvoiceSuccessAction

/**
 * redux action constants
 */
export const MARK_FINALIZED_INCOMING_INVOICE = "MARK_FINALIZED_INCOMING_INVOICES";
export const MARK_FINALIZED_INCOMING_INVOICE_SUCCESS = "MARK_FINALIZED_INCOMING_INVOICES_SUCCESS";
export const MARK_FINALIZED_INCOMING_INVOICE_ERROR = "MARK_FINALIZED_INCOMING_INVOICES_ERROR";

/**
 * redux action functions
 */
export function markFinalizedIncomingInvoice( incomingInvoiceId: Identifier< number > ) : MarkFinalizedIncomingInvoiceAction {
    return { type: MARK_FINALIZED_INCOMING_INVOICE, invoiceId: incomingInvoiceId };
}
export function markFinalizedIncomingInvoiceSuccess() : MarkFinalizedIncomingInvoiceSuccessAction {
    return { type: MARK_FINALIZED_INCOMING_INVOICE_SUCCESS };
}
export function markFinalizedIncomingInvoiceError( error: Error ): MarkFinalizedIncomingInvoiceErrorAction {
    return { type: MARK_FINALIZED_INCOMING_INVOICE_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function markFinalizedIncomingInvoiceEpic(
    action$: Observable< MarkFinalizedIncomingInvoiceAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( MARK_FINALIZED_INCOMING_INVOICE ),
        switchMap(action => {
            const path = `${ apiPath( action.invoiceId.rawValue ) }/final`;

            return ajax(
                APP_CONFIG.getRequestObject( path, RouteMethods.patch )
            ).pipe(
                mergeMap(_ => of(
                    markFinalizedIncomingInvoiceSuccess() ,
                    fetchIncomingInvoiceById( action.invoiceId ),
                    fetchIncomingInvoices( getInitialViewSettings( IncomingInvoiceModule.identifier ) )
                )),
                catchError(error => of(
                    markFinalizedIncomingInvoiceError( error),
                    eventNotificationError(error, store$, IncomingInvoiceModule.identifier, "markFinalizedIncomingInvoice")
                ))
            );

        })
    );
}



/**
 * FETCH FILES INVOICE
 */
export interface FetchInInvoiceFilesByIdAction extends IncomingInvoiceId, ReduxAction< typeof FETCH_INCOMING_INVOICE_FILES_BY_ID > {

}
export interface FetchInInvoiceFilesByIdSuccessAction extends IncomingInvoiceId, ReduxAction<typeof FETCH_INCOMING_INVOICE_FILES_BY_ID_SUCCESS > {
    payload: FileData[]

}
export interface FetchInInvoiceFilesByIdErrorAction extends IncomingInvoiceId, ErrorAction< typeof FETCH_INCOMING_INVOICE_FILES_BY_ID_ERROR >{}

export type FetchInInvoiceFilesByIdActionTypes = FetchInInvoiceFilesByIdAction | FetchInInvoiceFilesByIdErrorAction | FetchInInvoiceFilesByIdSuccessAction

/**
 * redux action constants
 */
export const FETCH_INCOMING_INVOICE_FILES_BY_ID = "FETCH_INCOMING_INVOICE_FILES_BY_ID";
export const FETCH_INCOMING_INVOICE_FILES_BY_ID_SUCCESS = "FETCH_INCOMING_INVOICE_FILES_BY_ID_SUCCESS";
export const FETCH_INCOMING_INVOICE_FILES_BY_ID_ERROR = "FETCH_INCOMING_INVOICE_FILES_BY_ID_ERROR";

/**
 * redux action functions
 */
export function fetchInInvoiceFilesById( incomingInvoiceId: Identifier<number> ) : FetchInInvoiceFilesByIdAction {
    return { type: FETCH_INCOMING_INVOICE_FILES_BY_ID, invoiceId: incomingInvoiceId };
}
export function fetchInInvoiceFilesByIdSuccess( payload: FileData[], incomingInvoiceId: Identifier<number> ) : FetchInInvoiceFilesByIdSuccessAction {
    return { type: FETCH_INCOMING_INVOICE_FILES_BY_ID_SUCCESS, payload, invoiceId: incomingInvoiceId };
}
export function fetchInInvoiceFilesByIdError(
    incomingInvoiceId: Identifier< number >,
    error: Error
): FetchInInvoiceFilesByIdErrorAction {
    return { type: FETCH_INCOMING_INVOICE_FILES_BY_ID_ERROR, error, invoiceId: incomingInvoiceId };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchInInvoiceFilesByIdEpic(
    action$: Observable< FetchInInvoiceFilesByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_INCOMING_INVOICE_FILES_BY_ID),
        switchMap(action => {
            const path = apiPath( action.invoiceId.rawValue )+"/files";

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchInInvoiceFilesByIdSuccess( response.response as FileData[], action.invoiceId )),
                catchError(error => of(
                    fetchInInvoiceFilesByIdError( action.invoiceId, error),
                    eventNotificationError(error, store$, IncomingInvoiceModule.identifier, "fetchInInvoiceFilesById")
                ))
            );

        })
    );
}


/**
 * DELETE IN PROCESS INVOICE
 */
export interface DeleteIncomingInvoiceByIdAction extends IncomingInvoiceId, ReduxAction< typeof DELETE_INCOMING_INVOICE_BY_ID > {

}
export interface DeleteIncomingInvoiceByIdSuccessAction extends ReduxAction<typeof DELETE_INCOMING_INVOICE_BY_ID_SUCCESS > {}
export interface DeleteIncomingInvoiceByIdErrorAction extends IncomingInvoiceId, ErrorAction< typeof DELETE_INCOMING_INVOICE_BY_ID_ERROR >{}

export type DeleteIncomingInvoiceByIdActionTypes = DeleteIncomingInvoiceByIdAction | DeleteIncomingInvoiceByIdErrorAction | DeleteIncomingInvoiceByIdSuccessAction

/**
 * redux action constants
 */
export const DELETE_INCOMING_INVOICE_BY_ID = "DELETE_INCOMING_INVOICE_BY_ID";
export const DELETE_INCOMING_INVOICE_BY_ID_SUCCESS = "DELETE_INCOMING_INVOICE_BY_ID_SUCCESS";
export const DELETE_INCOMING_INVOICE_BY_ID_ERROR = "DELETE_INCOMING_INVOICE_BY_ID_ERROR";

/**
 * redux action functions
 */
export function deleteIncomingInvoiceById( incomingInvoiceId: Identifier<number> ) : DeleteIncomingInvoiceByIdAction {
    return { type: DELETE_INCOMING_INVOICE_BY_ID, invoiceId: incomingInvoiceId };
}
export function deleteIncomingInvoiceByIdSuccess() : DeleteIncomingInvoiceByIdSuccessAction {
    return { type: DELETE_INCOMING_INVOICE_BY_ID_SUCCESS };
}
export function deleteIncomingInvoiceByIdError(
    incomingInvoiceId: Identifier< number >,
    error: Error
): DeleteIncomingInvoiceByIdErrorAction {
    return { type: DELETE_INCOMING_INVOICE_BY_ID_ERROR, error, invoiceId: incomingInvoiceId };
}

/**
 * redux observable to delete bills from api
 */
export function deleteIncomingInvoiceByIdEpic(
    action$: Observable< DeleteIncomingInvoiceByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(DELETE_INCOMING_INVOICE_BY_ID),
        switchMap(action => {
            const path = apiPath( action.invoiceId.rawValue );

            return ajax(
                APP_CONFIG.getRequestObject( path, RouteMethods.delete )
            ).pipe(
                mergeMap(response => of(
                    deleteIncomingInvoiceByIdSuccess(),
                    fetchIncomingInvoices( getInitialViewSettings( IncomingInvoiceModule.identifier ) ),
                    sendUserToPage(
                        RouteConfig.incomingInvoice.routes.main.path,
                        IncomingInvoiceModule.identifier
                    )
                )),
                catchError(error => of(
                    deleteIncomingInvoiceByIdError( action.invoiceId, error),
                    eventNotificationError(error, store$, IncomingInvoiceModule.identifier, "deleteIncomingInvoiceById")
                ))
            );

        })
    );
}
